import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';

import { ApolloCache, ApolloClient, createHttpLink, from, Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';
import { ENVIRONMENT } from '../constants/env-constants';
import { getAppAuthBoundary } from '../modules/auth';
import { GraphQLClient, validateTokenExpiration } from '@rapid-recovery-agency-inc/insightt-shared';
import { createApolloCache } from './apollo-cache';
import { getOwnProps, logoutSignOutAndCleanup } from './utils';
import { errors } from './strings';
import { getAwsRum } from './monitoring';
import { Query } from '@rapid-recovery-agency-inc/insightt-graphql';
import { InsighttNotification } from '../helpers/insightt-notification';
import { RumEvent } from './analytics/rum';
import { isPublicOptionalOperation } from './apollo-operation-context';

let connectErrorTimeout: ReturnType<typeof setTimeout> | null = null;
let noInternetConnectionTimeout: ReturnType<typeof setTimeout> | null = null;

const isPublicUserPath = (pathname: string) =>
  pathname.startsWith('/user/login') ||
  pathname.startsWith('/user/logout') ||
  pathname.startsWith('/user/forgot-password') ||
  pathname.startsWith('/user/reset-password') ||
  pathname.startsWith('/user/invitation');

function showConnectErrorNotification() {
  if (!connectErrorTimeout) {
    InsighttNotification(
      'error',
      "It seems that we can't connect to the server. Please check your internet connection, refresh your browser and try again. If the problem persists, please contact customer.support.83@insightt.io for additional help.",
    );
    connectErrorTimeout = setTimeout(() => {
      connectErrorTimeout = null;
    }, 10000); // 10 seconds
  }
}

function showNoInternetConnectionNotification() {
  if (!noInternetConnectionTimeout) {
    InsighttNotification('error', 'No internet connection detected');
    noInternetConnectionTimeout = setTimeout(() => {
      noInternetConnectionTimeout = null;
    }, 10000); // 10 seconds
  }
}

const retryLink = new RetryLink({
  delay: {
    initial: 1000,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error) => {
      return error.message === errors.FAILED_TO_FETCH;
    },
  },
});

// Exported (T028) so `apollo-auth-boundary.test.ts` can drive the unauthorized-episode path in
// isolation instead of exercising the whole composed `client`.
export const handleErrorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  // A public, optional request handles its own failures (#4193); see `apollo-operation-context.ts`.
  if (isPublicOptionalOperation(operation.getContext())) {
    return;
  }

  // A GraphQL or network error callback can run after a test environment (and its `window`) is
  // torn down; there is no sign-out, navigation or telemetry to perform in that case.
  if (typeof window === 'undefined') return;

  if (networkError?.message.includes(errors.FAILED_TO_FETCH)) {
    fetch('https://www.google.com', { mode: 'no-cors' })
      .then(() => {
        showConnectErrorNotification();

        getAwsRum().recordEvent(RumEvent.NETWORK_ERROR, {
          message: 'Network error: Failed to fetch',
          error: JSON.stringify(getOwnProps(networkError)),
        });
        return;
      })
      .catch((e) => {
        if (e.message.includes('Failed to fetch')) {
          showNoInternetConnectionNotification();
        }
        return;
      });
  }

  if (graphQLErrors) {
    getAwsRum().recordEvent(RumEvent.GRAPHQL_ERROR, {
      graphQLErrors,
      networkError: JSON.stringify(getOwnProps(networkError || {})),
      operation: operation?.operationName,
    });

    for (const { message } of graphQLErrors) {
      if (message.includes(errors.ACCESS_DENIED) && !isPublicUserPath(window.location.pathname)) {
        // One unauthorized episode, one owner (T066): full local cleanup, credential and provider
        // termination, then exactly one navigation. Fire-and-forget: this is a synchronous error
        // callback, and `signOut()` never rejects.
        logoutSignOutAndCleanup();
        return;
      }
    }
  }

  if (networkError) {
    if (
      networkError.message.includes(errors.ABORT_REQUEST) ||
      networkError.message.includes(errors.NETWORK_ERROR)
    ) {
      /*
      When the request is aborted, it is necessary to stop the flow
      here or else an error will be transmitted that exists in
      Apollo-client, specifically in the toQueryResult function.
      This is already documented in different issues, such as
      https://github.com/apollographql/apollo-client/issues/11251.
      */
      return;
    }

    getAwsRum().recordEvent(RumEvent.NETWORK_ERROR, {
      graphQLErrors,
      networkError: JSON.stringify(getOwnProps(networkError || {})),
      operation: operation?.operationName,
    });

    if (networkError.message.includes(errors.SESSION_TIMEOUT)) {
      logoutSignOutAndCleanup();
      return;
    }

    if (networkError.message === errors.FAILED_TO_FETCH) {
      return;
    }

    return new Observable((observer) => {
      observer.complete();
      return;
    });
  }
  return forward(operation);
});

const httpLink = createHttpLink({
  uri: `${ENVIRONMENT.BACKEND_SERVER}/graphql`,
});

const persistedQueryLink = createPersistedQueryLink({
  useGETForHashedQueries: true,
  sha256,
}).concat(httpLink);

/**
 * Bearer access + pre-request renewal, routed entirely through the mode-neutral
 * {@link getAppAuthBoundary} boundary (T028; FR-006, FR-008).
 *
 * `ensureFreshSession()` runs FIRST — before `getAccessToken()` reads the token that actually gets
 * sent — so an expiring SSO session gets its one silent-renewal attempt before this request goes
 * out, and the header carries whatever token is current AFTER that attempt settles (fresh on
 * success, whatever was already there if renewal was not needed/available). This is deliberately
 * one `setContext` link rather than a separate link ahead of it: `setContext`'s callback already
 * runs once per outgoing operation (including a `retryLink` retry) immediately before headers are
 * read, which is exactly the "before/on expiry, not just on first request" timing this story
 * requires (spec.md US3) — a second, separate link would only add an extra hop with no behavior
 * difference.
 *
 * For a legacy-only (or absent) session, `ensureFreshSession()` resolves immediately from already-
 * known state (`auth-boundary.ts`'s doc comment — "no `prompt=none` concept for legacy") without
 * starting an episode, so this preserves the exact current legacy behavior of just reading whatever
 * token is present.
 *
 * Concurrent requests (T028's "request-generation coverage"): multiple Apollo operations in flight
 * at once each call `ensureFreshSession()`, but never trigger more than one actual renewal attempt
 * between them — that de-duplication is `auth-boundary.ts`'s `renewalEpisode` single-flight
 * primitive (T027), which this link relies on rather than reimplements. See
 * `apollo-auth-boundary.test.ts` for the concurrency proof.
 *
 * The token-expiration guard below is kept as a defensive fallback (not the primary mechanism it
 * used to be): it now only fires when a token is present but stale AFTER `ensureFreshSession()` has
 * already had its chance to renew it — e.g. a legacy cookie nearing expiry (no renewal concept), or
 * an SSO renewal that settled `interaction_required` while an old token is still technically
 * present. Preserves the existing `SESSION_TIMEOUT` behavior byte-for-byte for a legacy session.
 *
 * A public, optional request (`apollo-operation-context.ts`, #4193) skips all of this: it sends no
 * credentials and never renews or ends a session.
 */
export const authLink = setContext(async (_, context) => {
  const { headers = {} } = context;
  if (isPublicOptionalOperation(context)) {
    return { headers };
  }

  const authBoundary = getAppAuthBoundary();

  await authBoundary.ensureFreshSession();

  const token = authBoundary.getAccessToken();

  if (token) {
    const expiredToken = validateTokenExpiration(token);
    if (expiredToken === true) {
      throw new Error(errors.SESSION_TIMEOUT);
    }
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const client = new ApolloClient({
  link: from(
    ENVIRONMENT.ENABLE_PERSISTED_QUERIES
      ? [handleErrorLink, retryLink.concat(authLink.concat(persistedQueryLink))]
      : [handleErrorLink, retryLink.concat(authLink.concat(httpLink))],
  ),
  cache: createApolloCache(),
}) as unknown as GraphQLClient;

const evictRootQueryCache = (fieldName: string) => {
  client.cache.evict({ id: 'ROOT_QUERY', fieldName });
  client.cache.gc();
};
type QueryParam = keyof Query;

export function forceRefetchQueries(queries: QueryParam[] | QueryParam) {
  return (cache: ApolloCache<unknown>) => {
    if (Array.isArray(queries)) {
      queries.forEach((query) => {
        cache.evict({ fieldName: query });
      });
    } else {
      cache.evict({ fieldName: queries });
    }
  };
}

export enum FetchPolicy {
  CACHE_FIRST = 'cache-first',
  CACHE_AND_NETWORK = 'cache-and-network',
  NETWORK_ONLY = 'network-only',
  CACHE_ONLY = 'cache-only',
  NO_CACHE = 'no-cache',
  STALE = 'stale',
}

export { client, evictRootQueryCache };
